import React from 'react';
import styles from './css/index.module.css';
import { Input, Select, Textarea } from '@fluentui/react-components';
import { PatchProjectData } from '../../redux/projectDataSlice';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import ReactSelect from 'react-select';

const AddProject = ({ compareObjectsValues }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = React.useState({
        project_name: '',
        investor_name: '',
        project_deadline: '',
        project_type: '',
        status: '',
        project_scope: [],
        comments: '',
        project_manager: '',
        lead_consultant: '',
        support_consultants: [],
        key_investor_name: '',
        key_investor_email: '',
    });

    const { projectDetailButtonLoading, selectedProject } = useSelector(state => state.projectData);
    const { projectUsersAll } = useSelector(state => state.team);
    const memberships = useSelector(state => state?.user?.memberships || []);
    const activeWorkspaceId = useSelector(state => state?.user?.activeWorkspaceId) || localStorage.getItem('active_workspace_id');

    const activeTenant = React.useMemo(() => {
        const m = memberships.find(mem => mem?.workspace?.id === activeWorkspaceId);
        return m?.workspace || null;
    }, [memberships, activeWorkspaceId]);

    const fallbackProjectTypeOptions = React.useMemo(
        () => [
            { value: 'Tech DD', text: 'Tech DD' },
            { value: 'IT M&A', text: 'IT M&A' },
            { value: 'Tech Advisory', text: 'Tech Advisory' },
            { value: 'Vendor DD', text: 'Vendor DD' },
            { value: 'DD & Post Merger', text: 'DD & Post Merger' },
            { value: 'Coaching', text: 'Coaching' },
            { value: 'Custom', text: 'Custom' },
        ],
        [],
    );

    const fallbackProjectStatusOptions = React.useMemo(
        () => [
            { value: 'Discussing', text: 'Discussing' },
            { value: 'Scoping', text: 'Scoping' },
            { value: 'Proposal Sent', text: 'Proposal Sent' },
            { value: 'Active', text: 'Active' },
            { value: 'Report Issued', text: 'Report Issued' },
            { value: 'Closed', text: 'Closed' },
            { value: 'Invoicing', text: 'Invoicing' },
            { value: 'On Hold', text: 'On Hold' },
            { value: 'Lost', text: 'Lost' },
        ],
        [],
    );

    const projectTypeOptions = React.useMemo(() => {
        const opts = activeTenant?.project_type_options;
        if (Array.isArray(opts) && opts.length) {
            return opts
                .filter(o => o && o.name)
                .map(o => ({
                    value: o.name,
                    text: o.name,
                    color: o.color,
                }));
        }
        return fallbackProjectTypeOptions;
    }, [activeTenant, fallbackProjectTypeOptions]);

    const projectStatusOptions = React.useMemo(() => {
        const opts = activeTenant?.project_status_options;
        if (Array.isArray(opts) && opts.length) {
            return opts
                .filter(o => o && o.name)
                .map(o => ({
                    value: o.name,
                    text: o.name,
                    color: o.color,
                }));
        }
        return fallbackProjectStatusOptions;
    }, [activeTenant, fallbackProjectStatusOptions]);

    const handleSubmit = async e => {
        e.preventDefault();
        // Filter out empty scope items before submitting
        const filteredProjectScope = formData.project_scope.filter(item => item.scopeArea !== '' || item.scopeItems !== '');

        // Clean bullet points from scope items before submitting
        const cleanedProjectScope = filteredProjectScope.map(item => ({
            ...item,
            scopeItems: item.scopeItems ? item.scopeItems.replace(/^[•\-*]\s*/gm, '') : '',
        }));

        const submitData = {
            ...formData,
            project_scope: cleanedProjectScope,
            project_deadline: format(new Date(formData?.project_deadline), 'dd-MM-yyyy'),
            project_manager: formData?.project_manager || null,
            lead_consultant: formData?.lead_consultant || null,
        };
        dispatch(
            PatchProjectData({
                data: submitData,
                id: selectedProject?.id,
            }),
        );
    };

    const handleChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleProjectScopeChange = (index, field, value) => {
        const updatedScope = [...formData.project_scope];
        updatedScope[index] = { ...updatedScope[index], [field]: value };

        // Only remove completely empty items (both scopeArea and scopeItems are empty)
        const filteredScope = updatedScope.filter((item, idx) => item.scopeArea !== '' || item.scopeItems !== '' || idx === updatedScope.length - 1);

        setFormData({ ...formData, project_scope: filteredScope });
    };

    // Enhanced scope items change handler with bullet point functionality
    const handleScopeItemsChange = (index, value) => {
        let formattedValue = value;

        // If user is typing the first word and field doesn't start with bullet point, add one
        if (value && !value.startsWith('•') && !value.startsWith('-') && !value.startsWith('*')) {
            const lines = value.split('\n');
            const firstLine = lines[0];

            // If first line has content but no bullet point, add one
            if (firstLine.trim() !== '' && !firstLine.trim().match(/^[•\-*]\s/)) {
                lines[0] = '• ' + firstLine;
                formattedValue = lines.join('\n');
            }
        } else if (value && value.trim() === '') {
            // If field is empty, don't add bullet point yet
            formattedValue = '';
        }

        const updatedScope = [...formData.project_scope];
        updatedScope[index] = { ...updatedScope[index], scopeItems: formattedValue };

        // Only remove completely empty items (both scopeArea and scopeItems are empty)
        const filteredScope = updatedScope.filter((item, idx) => item.scopeArea !== '' || item.scopeItems !== '' || idx === updatedScope.length - 1);

        setFormData({ ...formData, project_scope: filteredScope });
    };

    // Handle key press in scope items textarea
    const handleScopeItemsKeyDown = (index, e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const currentValue = formData.project_scope[index].scopeItems;
            const cursorPosition = e.target.selectionStart;

            // Split the text at cursor position
            const beforeCursor = currentValue.substring(0, cursorPosition);
            const afterCursor = currentValue.substring(cursorPosition);

            // Add bullet point to new line
            const newValue = beforeCursor + '\n• ' + afterCursor;

            // Update the value
            handleScopeItemsChange(index, newValue);

            // Set cursor position after the bullet point
            setTimeout(() => {
                const newCursorPosition = cursorPosition + 3; // 3 characters: '\n• '
                e.target.setSelectionRange(newCursorPosition, newCursorPosition);
            }, 0);
        }
    };

    // Handle focus events to ensure first line has bullet point
    const handleScopeItemsFocus = (index, e) => {
        const currentValue = formData.project_scope[index].scopeItems;

        // If the field is completely empty, add a bullet point
        if (!currentValue || currentValue.trim() === '') {
            handleScopeItemsChange(index, '• ');

            // Set cursor position after the bullet point
            setTimeout(() => {
                e.target.setSelectionRange(2, 2); // Position after "• "
            }, 0);
        }
    };

    // Handle paste events to format pasted content
    const handleScopeItemsPaste = (index, e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const currentValue = formData.project_scope[index].scopeItems;
        const cursorPosition = e.target.selectionStart;

        // Split the text at cursor position
        const beforeCursor = currentValue.substring(0, cursorPosition);
        const afterCursor = currentValue.substring(cursorPosition);

        // Format pasted text to ensure bullet points
        const formattedPastedText = formatScopeItems(pastedText);

        // Combine the text
        const newValue = beforeCursor + formattedPastedText + afterCursor;

        // Update the value
        handleScopeItemsChange(index, newValue);

        // Set cursor position after pasted content
        setTimeout(() => {
            const newCursorPosition = cursorPosition + formattedPastedText.length;
            e.target.setSelectionRange(newCursorPosition, newCursorPosition);
        }, 0);
    };

    // Format scope items to ensure each line starts with a bullet point
    const formatScopeItems = React.useCallback(scopeItems => {
        if (!scopeItems) return '';

        // Split by lines and ensure each non-empty line starts with a bullet point
        const lines = scopeItems.split('\n');
        const formattedLines = lines.map(line => {
            const trimmedLine = line.trim();
            if (trimmedLine === '') return '';

            // If line doesn't start with a bullet point, add one
            if (!trimmedLine.match(/^[•\-*]\s/)) {
                return '• ' + trimmedLine;
            }
            return line;
        });

        return formattedLines.join('\n');
    }, []);

    const addProjectScopeItem = () => {
        setFormData({
            ...formData,
            project_scope: [...formData.project_scope, { scopeArea: '', scopeItems: '' }],
        });
    };

    const handleSetFormValues = React.useCallback(() => {
        // Handle both old string format and new array format
        let projectScope = selectedProject?.project_scope;
        if (typeof projectScope === 'string') {
            // Convert old string format to new array format
            projectScope = [{ scopeArea: 'General Scope', scopeItems: projectScope }];
        } else if (!Array.isArray(projectScope)) {
            projectScope = [];
        } else if (projectScope.length === 0) {
            // Keep as empty array, don't add empty object
            projectScope = [];
        }

        // Format scope items to ensure bullet points
        const formattedProjectScope = projectScope.map(item => ({
            ...item,
            scopeItems: item.scopeItems ? formatScopeItems(item.scopeItems) : '',
        }));

        setFormData({
            project_name: selectedProject?.project_name || '',
            investor_name: selectedProject?.investor_name || '',
            project_deadline: selectedProject?.project_deadline || '',
            project_type: selectedProject?.project_type || '',
            status: selectedProject?.status || '',
            project_scope: formattedProjectScope,
            comments: selectedProject?.comments || '',
            project_manager: selectedProject?.project_manager || '',
            lead_consultant: selectedProject?.lead_consultant || '',
            support_consultants: selectedProject?.support_consultants || [],
            key_investor_name: selectedProject?.key_investor_name || '',
            key_investor_email: selectedProject?.key_investor_email || '',
        });
    }, [selectedProject, formatScopeItems]);

    React.useEffect(() => {
        if (selectedProject) {
            handleSetFormValues();
        }
    }, [selectedProject, handleSetFormValues]);

    return (
        <div className={styles.componentCard}>
            <h5 className={styles.componentTitle + ' mt-0'}>Project details</h5>
            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <p className={styles.fieldLabel}>
                        Project Name <span className={styles.required}>*</span>
                    </p>
                    <Input className={styles.inputField} required value={formData?.project_name} onChange={handleChange} name="project_name" />
                </div>
                <div className="mb-4">
                    <p className={styles.fieldLabel}>Investor Name</p>
                    <Input className={styles.inputField} required value={formData?.investor_name} onChange={handleChange} name="investor_name" />
                </div>

                <div className="d-flex gap-3 mb-4">
                    <div className="flex-grow-1">
                        <p className={styles.fieldLabel}>Key Investor Name</p>
                        <Input
                            className={styles.inputField}
                            value={formData?.key_investor_name}
                            onChange={handleChange}
                            name="key_investor_name"
                            placeholder="Enter investor name"
                        />
                    </div>
                    <div className="flex-grow-1">
                        <p className={styles.fieldLabel}>Key Investor Email</p>
                        <Input
                            className={styles.inputField}
                            value={formData?.key_investor_email}
                            onChange={handleChange}
                            name="key_investor_email"
                            type="email"
                            placeholder="Enter investor email"
                        />
                    </div>
                </div>

                <div className="d-flex gap-3 mb-4">
                    <div className="flex-grow-1">
                        <p className={styles.fieldLabel}>Status</p>
                        <Select className={styles.selectField} value={formData?.status} onChange={e => setFormData({ ...formData, status: e.target.value })} name="status">
                            <option value="">Select Status</option>
                            {projectStatusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.text}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div className="flex-grow-1">
                        <p className={styles.fieldLabel}>
                            Project Deadline <span className={styles.required}>*</span>
                        </p>
                        <Input className={styles.inputField} required type="date" value={formData?.project_deadline} onChange={handleChange} name="project_deadline" />
                    </div>
                </div>

                <div className="d-flex gap-3 mb-4">
                    <div className="flex-grow-1">
                        <p className={styles.fieldLabel}>
                            Project Type <span className={styles.required}>*</span>
                        </p>
                        <Select
                            className={styles.selectField}
                            required
                            value={formData?.project_type}
                            onChange={e => setFormData({ ...formData, project_type: e.target.value })}
                            name="project_type">
                            <option value="">Select Project Type</option>
                            {projectTypeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.text}
                                </option>
                            ))}
                        </Select>
                    </div>
                </div>

                <div className="mb-4">
                    <p className={styles.fieldLabel}>Project Scope</p>
                    {formData.project_scope.map((item, index) => (
                        <div key={index} className="mb-3 p-3 border rounded">
                            <div className="mb-2">
                                <p className={styles.fieldLabel}>Scope Area</p>
                                <Input
                                    className={styles.inputField}
                                    value={item.scopeArea}
                                    onChange={e => handleProjectScopeChange(index, 'scopeArea', e.target.value)}
                                    placeholder="e.g., Technical Assessment"
                                />
                            </div>
                            <div className="mb-2">
                                <p className={styles.fieldLabel}>Scope Items</p>
                                <Textarea
                                    className={styles.textareaField}
                                    value={item.scopeItems}
                                    onChange={e => handleScopeItemsChange(index, e.target.value)}
                                    onKeyDown={e => handleScopeItemsKeyDown(index, e)}
                                    onFocus={e => handleScopeItemsFocus(index, e)}
                                    onPaste={e => handleScopeItemsPaste(index, e)}
                                    placeholder="• Code review&#10;• Architecture analysis&#10;• Performance testing&#10;&#10;Press Enter to automatically add bullet points (•) to new lines"
                                    rows={4}
                                />
                            </div>
                        </div>
                    ))}
                    <p onClick={addProjectScopeItem} className={styles.linkBtn}>
                        Add another scope area
                    </p>
                </div>
                <div className={styles.divider}></div>

                <div className="mb-4">
                    <p className={styles.fieldLabel}>Comments</p>
                    <Textarea className={styles.textareaField} value={formData?.comments} onChange={handleChange} name="comments" rows={4} />
                </div>

                <div className={styles.divider}></div>
                <div className="mb-4">
                    <p className={styles.fieldLabel}>Project Manager</p>
                    <ReactSelect
                        className={styles.selectField}
                        value={
                            projectUsersAll?.find(item => item?.value?.group_member_id === formData?.project_manager)
                                ? {
                                      value: formData?.project_manager,
                                      label: projectUsersAll?.find(item => item?.value?.group_member_id === formData?.project_manager)?.value?.full_name,
                                  }
                                : null
                        }
                        onChange={selectedOption => setFormData({ ...formData, project_manager: selectedOption?.value || null })}
                        options={projectUsersAll?.map(item => ({
                            value: item?.value?.group_member_id,
                            label: item?.value?.full_name,
                        }))}
                        isClearable
                        placeholder="Select Project Manager"
                    />
                </div>
                <div className="mb-4">
                    <p className={styles.fieldLabel}>Lead Consultant</p>
                    <ReactSelect
                        className={styles.selectField}
                        value={
                            projectUsersAll?.find(item => item?.value?.group_member_id === formData?.lead_consultant)
                                ? {
                                      value: formData?.lead_consultant,
                                      label: projectUsersAll?.find(item => item?.value?.group_member_id === formData?.lead_consultant)?.value?.full_name,
                                  }
                                : null
                        }
                        onChange={selectedOption => setFormData({ ...formData, lead_consultant: selectedOption?.value || null })}
                        options={projectUsersAll
                            ?.filter(item => !formData?.support_consultants?.includes(item?.value?.group_member_id))
                            ?.map(item => ({
                                value: item?.value?.group_member_id,
                                label: item?.value?.full_name,
                            }))}
                        isClearable
                        placeholder="Select Lead Consultant"
                    />
                </div>

                <div className="mb-4">
                    <p className={styles.fieldLabel}>Support Consultants</p>
                    <ReactSelect
                        className={styles.selectField}
                        value={formData?.support_consultants
                            ?.map(consultantId => {
                                const consultant = projectUsersAll?.find(item => item?.value?.group_member_id === consultantId);
                                return consultant
                                    ? {
                                          value: consultantId,
                                          label: consultant?.value?.full_name,
                                      }
                                    : null;
                            })
                            .filter(Boolean)}
                        onChange={selectedOptions =>
                            setFormData({
                                ...formData,
                                support_consultants: selectedOptions?.map(option => option.value) || [],
                            })
                        }
                        options={projectUsersAll
                            ?.filter(item => item?.value?.group_member_id !== formData?.lead_consultant)
                            ?.map(item => ({
                                value: item?.value?.group_member_id,
                                label: item?.value?.full_name,
                            }))}
                        isMulti
                        isClearable
                        placeholder="Select Support Consultants"
                    />
                </div>

                <div className={styles.buttonDiv}>
                    {!compareObjectsValues(selectedProject, formData) && (
                        <div className="d-flex align-items-center justify-content-between">
                            <button disabled={!formData?.project_name || !formData?.project_deadline || !formData?.project_type} type="submit" className={styles.submitButton}>
                                Save
                                {projectDetailButtonLoading && <AiOutlineLoading3Quarters className="ms-2 rotateLoader" />}
                            </button>
                            <span onClick={handleSetFormValues} className={styles.linkBtn}>
                                Revert
                            </span>
                        </div>
                    )}
                </div>
            </form>
        </div>
    );
};

export default AddProject;
